import { useEffect, useState } from "react";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Info } from "lucide-react";
import { fetchIncidentExplain } from "../api/incidents.js";

const CODE_TONE = {
  notified: { bg: "bg-emerald-50", text: "text-emerald-800", Icon: CheckCircle2 },
  not_notified: { bg: "bg-amber-50", text: "text-amber-800", Icon: AlertCircle },
  dependency_down: { bg: "bg-indigo-50", text: "text-indigo-800", Icon: Info },
  group_already_notified: { bg: "bg-indigo-50", text: "text-indigo-800", Icon: Info },
  not_alertable: { bg: "bg-gray-50", text: "text-gray-700", Icon: Info },
  regional_only: { bg: "bg-amber-50", text: "text-amber-800", Icon: Info },
  global_failure: { bg: "bg-red-50", text: "text-red-800", Icon: AlertCircle },
};

function ReasonRow({ reason }) {
  const tone = CODE_TONE[reason.code] || { bg: "bg-gray-50", text: "text-gray-700", Icon: Info };
  const Icon = tone.Icon;
  return (
    <li className={`flex gap-2 rounded-md px-2 py-1.5 ${tone.bg} ${tone.text}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium">{reason.title}</p>
        <p className="text-xs opacity-80">{reason.detail}</p>
      </div>
    </li>
  );
}

export default function IncidentExplainer({ incidentId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || data) return;
    setLoading(true);
    fetchIncidentExplain(incidentId)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, incidentId, data]);

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
      >
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Why this alert?
        {data?.headline && (
          <span className="ml-auto truncate text-xs font-normal text-gray-500">
            {data.headline}
          </span>
        )}
      </button>
      {open && (
        <div className="border-t border-gray-100 px-3 py-2">
          {loading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : !data ? (
            <p className="text-sm text-gray-400">No explanation available.</p>
          ) : (
            <>
              <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <span
                  className={`rounded-full px-2 py-0.5 font-medium ${
                    data.notification_sent
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  }`}
                >
                  {data.notification_sent ? "Notification sent" : "No notification sent"}
                </span>
                {data.regional_only && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-800">
                    Regional only
                  </span>
                )}
                {data.group_size > 1 && (
                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-medium text-indigo-800">
                    Group of {data.group_size}
                  </span>
                )}
                {data.parent_monitor && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700">
                    Depends on {data.parent_monitor}
                    {data.parent_has_live_incident && " (down)"}
                  </span>
                )}
              </div>
              <ul className="space-y-1.5">
                {(data.reasons || []).map((r, i) => (
                  <ReasonRow key={i} reason={r} />
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
